import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Select,
  InputNumber,
  Switch,
  Button,
  message,
  Alert,
  Divider,
  Space,
  Tag,
  Table,
  Input,
  Popconfirm,
  Row,
  Col,
  Tooltip,
  Grid,
  List,
  Modal,
  Radio,
  ConfigProvider
} from 'antd';
import {
  DatabaseOutlined,
  SaveOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  KeyOutlined,
  QuestionCircleOutlined,
  ClearOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import TagsInput, { isExtension, isQueryParam, isCookieName, isUrlOrPath } from '../../../shared/components/TagsInput';
import { DurationInput, CountInput, TTL_PRESETS } from '../../../shared/components/cdn/UnitInputs';
import { LadderSelect, WaitingRoomFields } from '../../../shared/components/cdn/ActionBuilder';
import { Folded } from '../../../shared/components/cdn/CustomResponseEditor';
import { PlanTag, planAllows, fieldLocked } from '../../../shared/components/cdn/PlanGate';

const { Option } = Select;
const { useBreakpoint } = Grid;

// 默认Cache Key设置
const DEFAULT_CACHE_KEY = {
  vary_by_device: false,
  vary_by_country: false,
  vary_by_language: false,
  cache_host_mode: 'original',
  vary_headers: []
};

// 历史库值 1/enable/disable 归一为表单三档(否则下拉显示原始值)
const normSwitch = (v) => {
  const s = String(v ?? '').toLowerCase();
  if (['on', '1', 'true', 'enable', 'enabled'].includes(s)) return 'on';
  if (['off', '0', 'false', 'disable', 'disabled'].includes(s)) return 'off';
  return 'auto';
};

// 预定义的文件类型缓存规则
const DEFAULT_FILE_TYPE_RULES = [
  { extensions: 'jpg,jpeg,png,gif,webp,ico,svg', name: '图片', ttl: 604800, enabled: true },
  { extensions: 'css', name: 'CSS样式', ttl: 604800, enabled: true },
  { extensions: 'js', name: 'JavaScript', ttl: 604800, enabled: true },
  { extensions: 'woff,woff2,ttf,eot,otf', name: '字体文件', ttl: 2592000, enabled: true },
  { extensions: 'mp4,webm,ogg,mp3,wav', name: '媒体文件', ttl: 604800, enabled: false },
  { extensions: 'pdf,doc,docx,xls,xlsx,zip,rar', name: '文档文件', ttl: 86400, enabled: false }
];

// 内联 monospace 代码样式
const CODE_STYLE = {
  fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
  background: 'rgba(0,0,0,0.06)',
  padding: '1px 5px',
  borderRadius: 4,
  fontSize: '0.85em'
};

// 根据 query 模式计算示例请求的 query 部分(纯前端演示,不落库)
function computeSampleQueryPart(mode, params, pairs) {
  if (mode === 'ignore') return '';
  let list = pairs;
  if (mode === 'whitelist') {
    list = params.length ? pairs.filter(([k]) => params.includes(k)) : [];
  } else if (mode === 'blacklist') {
    list = params.length ? pairs.filter(([k]) => !params.includes(k)) : pairs;
  }
  if (mode === 'sorted') {
    list = [...list].sort((a, b) => a[0].localeCompare(b[0]));
  }
  return list.map(([k, v]) => `${k}=${v}`).join('&');
}

// 缓存键预览:根据当前 query 模式 + vary 设置动态展示缓存键组成
function CacheKeyPreview({ queryMode, queryParams, cacheKey, isMobile }) {
  const { t } = useTranslation();
  // resolved 在边缘上等同 original(已从选项里去掉,旧配置按 original 显示)
  const hostMap = {
    original: t('host (visitor hostname)'),
    resolved: t('host (visitor hostname)'),
    host_header: t('host (origin Host)')
  };
  const params = (queryParams || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  const segments = [
    { key: 'scheme', label: 'scheme', color: 'geekblue', desc: t('Request protocol http / https') },
    {
      key: 'host',
      label: hostMap[cacheKey.cache_host_mode] || 'host',
      color: 'blue',
      desc: t('Domain (determined by the “Cache host mode” above)')
    },
    { key: 'path', label: 'path', color: 'blue', desc: t('URL path, e.g., /blog/post') }
  ];

  let q;
  switch (queryMode) {
    case 'ignore':
      q = { label: t('—(no query)'), color: 'default', desc: t('Ignore all query parameters: ?a=3 and ?a=4 share the same cached content') };
      break;
    case 'sorted':
      q = { label: t('query (sorted)'), color: 'green', desc: t('Include all parameters sorted by name: ?b=2&a=1 and ?a=1&b=2 use the same cache entry, but ?a=3 and ?a=4 still use separate entries') };
      break;
    case 'whitelist':
      q = {
        label: params.length ? t('query (only {{value}})', { value: params.join(', ') }) : t('query (empty allowlist → ignored)'),
        color: 'cyan',
        desc: t('Only allowlisted parameters are included in the cache key; all others are ignored')
      };
      break;
    case 'blacklist':
      q = {
        label: params.length ? t('query (exclude {{value}})', { value: params.join(', ') }) : t('query (full)'),
        color: 'orange',
        desc: t('Parameters on the blacklist are excluded from the cache key; all others are retained.')
      };
      break;
    default:
      q = { label: t('query (full)'), color: 'blue', desc: t('Keep all parameters unchanged: ?a=3 and ?a=4 use separate cache entries') };
  }
  segments.push({ key: 'query', ...q });

  if (cacheKey.vary_by_device) segments.push({ key: 'device', label: 'device', color: 'purple', desc: t('Cache mobile / desktop separately') });
  if (cacheKey.vary_by_country) segments.push({ key: 'country', label: 'country', color: 'purple', desc: t('Cache separately by visitor country / region') });
  if (cacheKey.vary_by_language) segments.push({ key: 'lang', label: 'lang', color: 'purple', desc: t('Cache separately by Accept-Language') });

  // 具体示例:https://example.com/blog?a=3&utm_source=ad&b=2
  const samplePairs = [['a', '3'], ['utm_source', 'ad'], ['b', '2']];
  const sampleQuery = computeSampleQueryPart(queryMode, params, samplePairs);
  const varySuffix = [
    cacheKey.vary_by_device ? 'device=mobile' : null,
    cacheKey.vary_by_country ? 'country=US' : null,
    cacheKey.vary_by_language ? 'lang=zh' : null
  ].filter(Boolean);
  const sampleKey =
    `https|example.com|/blog` +
    (sampleQuery ? `?${sampleQuery}` : '') +
    (varySuffix.length ? `|${varySuffix.join('|')}` : '');

  return (
    <div
      style={{
        marginTop: 8,
        marginBottom: 16,
        padding: isMobile ? 12 : 16,
        border: '1px dashed #91caff',
        borderRadius: 8,
        background: 'rgba(230,247,255,0.35)'
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        <Space>
          <KeyOutlined />{t('Cache key preview')}</Space>
      </div>
      <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: isMobile ? 12 : 13 }}>{t('With the current settings, each request gets a unique cache key made up of the parts below. If any part differs, separate cache entries are used.')}</div>

      {/* 公式:各段用 + 连接 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        {segments.map((s, i) => (
          <React.Fragment key={s.key}>
            {i > 0 && <span style={{ color: '#bfbfbf' }}>+</span>}
            <Tag color={s.color} style={{ margin: 0 }}>{s.label}</Tag>
          </React.Fragment>
        ))}
      </div>

      {/* 逐段说明 */}
      <div style={{ marginBottom: 12 }}>
        {segments.map((s) => (
          <div key={s.key} style={{ fontSize: isMobile ? 12 : 13, lineHeight: '22px', marginBottom: 2 }}>
            <Tag color={s.color} style={{ marginRight: 6 }}>{s.label}</Tag>
            <span style={{ color: '#8c8c8c' }}>{s.desc}</span>
          </div>
        ))}
      </div>

      {/* 具体示例 */}
      <div style={{ fontSize: isMobile ? 12 : 13, marginBottom: 6 }}>
        <span style={{ color: '#8c8c8c' }}>{t('Example request ')}</span>
        <code style={CODE_STYLE}>https://example.com/blog?a=3&utm_source=ad&b=2</code>
        <span style={{ color: '#8c8c8c' }}>{t(' \'s actual cache key:')}</span>
      </div>
      <code style={{ ...CODE_STYLE, display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-all', padding: '8px 10px' }}>
        {sampleKey}
      </code>

      <Alert
        style={{ marginTop: 12 }}
        type="info"
        showIcon
        message={
          <span style={{ fontSize: isMobile ? 12 : 13 }}>{t('Debug tip: include in the request ')}<code style={CODE_STYLE}>X-Debug-Cache: 1</code>{t(' header, the response returns ')}<code style={CODE_STYLE}>X-Cache-Key</code>{t(' header to view the actual cache key used by the edge node. (This debug header is implemented at the edge.)')}</span>
        }
      />
    </div>
  );
}

export default function CacheSettings() {
  const { t } = useTranslation();
  const { domainData, reloadDomain } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [fileTypeRules, setFileTypeRules] = useState(DEFAULT_FILE_TYPE_RULES);
  const [cacheKey, setCacheKey] = useState(DEFAULT_CACHE_KEY);
  // 高级缓存 / 负缓存 (status_ttl 存为对象,编辑用数组)
  const [statusTtlRules, setStatusTtlRules] = useState([]);
  // Origin Shield 回源盾 → settings.origin_shield
  const [originShield, setOriginShield] = useState({
    max_concurrency: 0,
    action: [],
    queue_max: 0,
    queue_wait_ms: 0,
    retry_after: 0
  });
  const [queryParams, setQueryParams] = useState('');
  const [purgeModalVisible, setPurgeModalVisible] = useState(false);
  const [purgeMode, setPurgeMode] = useState('all');
  const [purgeUrls, setPurgeUrls] = useState('');
  const [purging, setPurging] = useState(false);
  // 套餐缓存级别:缓存键 / 自定义 TTL 规则需 advanced(已保存的旧值保留,控件只读)
  const keyLocked = fieldLocked(domainData, 'cache_query_mode');
  const ttlLocked = fieldLocked(domainData, 'status_ttl');

  useEffect(() => {
    if (domainData) {
      // 加载文件类型缓存规则 - 从 domain 读取，不是 hostname
      const savedRules = domainData.cache_file_types
        ? (typeof domainData.cache_file_types === 'string'
          ? JSON.parse(domainData.cache_file_types)
          : domainData.cache_file_types)
        : []; // 未配置 = 边缘无扩展名规则;推荐规则需用户点选后保存才生效

      setFileTypeRules(savedRules);

      // 加载 Cache Key 设置 - 从 settings.cache_key 读取
      const savedCacheKey = domainData.settings?.cache_key || DEFAULT_CACHE_KEY;
      setCacheKey({
        vary_by_device: savedCacheKey.vary_by_device || false,
        vary_by_country: savedCacheKey.vary_by_country || false,
        vary_by_language: savedCacheKey.vary_by_language || false,
        cache_host_mode: savedCacheKey.cache_host_mode || 'original',
        vary_headers: savedCacheKey.vary_headers || [],
        // 高级缓存 / 负缓存
        min_ttl: savedCacheKey.min_ttl ?? 0,
        max_ttl: savedCacheKey.max_ttl ?? 0,
        stale_if_error: savedCacheKey.stale_if_error ?? 0,
        bypass_auth: !!savedCacheKey.bypass_auth,
        bypass_cookies: Array.isArray(savedCacheKey.bypass_cookies) ? savedCacheKey.bypass_cookies : [],
        respect_client_no_cache: savedCacheKey.respect_client_no_cache !== undefined ? !!savedCacheKey.respect_client_no_cache : false
      });

      // status_ttl 对象 {"404":30} → 编辑用数组 [{code,ttl}]
      const savedStatusTtl = savedCacheKey.status_ttl || {};
      setStatusTtlRules(
        Object.keys(savedStatusTtl).map((code) => ({ code: Number(code), ttl: Number(savedStatusTtl[code]) }))
      );

      // Origin Shield 回源盾 → settings.origin_shield
      const savedShield = domainData.settings?.origin_shield || {};
      setOriginShield({
        max_concurrency: savedShield.max_concurrency ?? 0,
        action: Array.isArray(savedShield.action)
          ? savedShield.action
          : (typeof savedShield.action === 'string' && savedShield.action
            ? savedShield.action.split(/[,|]/).map((s) => s.trim()).filter(Boolean)
            : []),
        queue_max: savedShield.queue_max ?? 0,
        queue_wait_ms: savedShield.queue_wait_ms ?? 0,
        retry_after: savedShield.retry_after ?? 0,
        waiting_room: savedShield.waiting_room && typeof savedShield.waiting_room === 'object' ? savedShield.waiting_room : {}
      });

      // 加载 Query String 参数
      const savedQueryParams = domainData.cache_query_params;
      if (savedQueryParams) {
        const params = typeof savedQueryParams === 'string'
          ? JSON.parse(savedQueryParams)
          : savedQueryParams;
        setQueryParams(Array.isArray(params) ? params.join(', ') : '');
      } else {
        setQueryParams('');
      }

      form.setFieldsValue({
        cache_enable: normSwitch(domainData.cache_enable),
        cache_expire: domainData.cache_expire || 3600,
        cache_ttl_mode: domainData.cache_ttl_mode === 'origin_only' ? 'origin' : (domainData.cache_ttl_mode || 'origin_or_default'),
        browser_cache: normSwitch(domainData.browser_cache),
        browser_cache_expire: domainData.browser_cache_expire || 7200,
        browser_cache_ttl_mode: domainData.browser_cache_ttl_mode === 'origin_only' ? 'origin_or_default' : (domainData.browser_cache_ttl_mode || 'origin_or_default'),
        cache_query_mode: domainData.cache_query_mode || 'standard',
        // Stale Cache 设置（默认为 true，即启用）
        stale_cache_enabled: domainData.stale_cache_enabled !== undefined
          ? !!domainData.stale_cache_enabled
          : true
      });
    }
  }, [domainData, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // 验证文件类型规则
      const invalidRules = fileTypeRules.filter(rule =>
        rule.enabled && (!rule.extensions || rule.extensions.trim() === '')
      );

      if (invalidRules.length > 0) {
        message.error(t('Enabled rules must specify file extensions'));
        setLoading(false);
        return;
      }

      // 过滤掉空的或禁用的规则
      const validRules = fileTypeRules.filter(rule =>
        rule.extensions && rule.extensions.trim() !== ''
      );

      // 准备 Query String 参数
      const queryParamsArray = queryParams
        ? queryParams.split(',').map(p => p.trim()).filter(p => p)
        : [];

      // 准备 settings.cache_key 对象
      // status_ttl 数组 → 对象 {"404":30}
      const statusTtlObj = {};
      statusTtlRules.forEach((r) => {
        const code = parseInt(r.code, 10);
        if (code >= 100 && code <= 599 && r.ttl != null && r.ttl !== '') {
          statusTtlObj[String(code)] = Number(r.ttl);
        }
      });

      const cacheKeySettings = {
        ...cacheKey,
        vary_headers: cacheKey.vary_headers || [],
        status_ttl: statusTtlObj,
        bypass_cookies: cacheKey.bypass_cookies || []
      };

      // Update domain cache settings (domain-level, not hostname-level)
      const response = await cdnService.updateDomain({
        id: domainData.id,
        cache_enable: values.cache_enable,
        cache_expire: values.cache_expire,
        cache_ttl_mode: values.cache_ttl_mode,
        browser_cache: values.browser_cache,
        browser_cache_expire: values.browser_cache_expire,
        browser_cache_ttl_mode: values.browser_cache_ttl_mode,
        cache_file_types: JSON.stringify(validRules),
        cache_query_mode: values.cache_query_mode,
        cache_query_params: JSON.stringify(queryParamsArray),
        stale_cache_enabled: values.stale_cache_enabled,
        settings: {
          ...domainData.settings,
          cache_key: cacheKeySettings,
          origin_shield: originShield
        }
      });

      if (response.success) {
        message.success(t('Cache settings updated'));
        reloadDomain();
      } else {
        message.error(response.error || t('Update failed'));
      }
    } catch (error) {
      console.error('Failed to update cache settings:', error);
      message.error(t('Update failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileTypeRuleChange = (index, field, value) => {
    const newRules = [...fileTypeRules];
    newRules[index] = { ...newRules[index], [field]: value };
    setFileTypeRules(newRules);
  };

  const handleAddFileTypeRule = () => {
    setFileTypeRules([
      ...fileTypeRules,
      { extensions: '', name: '自定义', ttl: 3600, enabled: true }
    ]);
  };

  const handleDeleteFileTypeRule = (index) => {
    const newRules = fileTypeRules.filter((_, i) => i !== index);
    setFileTypeRules(newRules);
  };

  // Cache Key 设置更新
  const handleCacheKeyChange = (field, value) => {
    setCacheKey(prev => ({ ...prev, [field]: value }));
  };

  // Origin Shield 回源盾更新
  const handleOriginShieldChange = (field, value) => {
    setOriginShield(prev => ({ ...prev, [field]: value }));
  };

  // 负缓存 status_ttl 行编辑
  const handleStatusTtlChange = (index, field, value) => {
    setStatusTtlRules(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const handleAddStatusTtl = () => setStatusTtlRules(prev => [...prev, { code: 404, ttl: 60 }]);
  const handleDeleteStatusTtl = (index) => setStatusTtlRules(prev => prev.filter((_, i) => i !== index));

  const fileTypeColumns = [
    {
      title: t('Status'),
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled, record, index) => (
        <Switch
          checked={enabled}
          onChange={(checked) => handleFileTypeRuleChange(index, 'enabled', checked)}
        />
      )
    },
    {
      title: t('Rule name'),
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (name, record, index) => (
        <Input
          value={t(name)}
          onChange={(e) => handleFileTypeRuleChange(index, 'name', e.target.value)}
          placeholder={t('Rule name')}
        />
      )
    },
    {
      title: t('File extension'),
      dataIndex: 'extensions',
      key: 'extensions',
      render: (extensions, record, index) => (
        <TagsInput
          format="comma"
          lowercase
          value={extensions}
          onChange={(v) => handleFileTypeRuleChange(index, 'extensions', v)}
          placeholder="jpg, png, gif"
          validate={(v) => (isExtension(v) ? null : t('Invalid file extension'))}
        />
      )
    },
    {
      title: t('Cache TTL'),
      dataIndex: 'ttl',
      key: 'ttl',
      width: 280,
      render: (ttl, record, index) => (
        <DurationInput
          value={ttl}
          onChange={(value) => handleFileTypeRuleChange(index, 'ttl', value)}
          presets={TTL_PRESETS}
          zeroLabel={t('No cache')}
          min={0}
          max={31536000}
        />
      )
    },
    {
      title: t('Actions'),
      key: 'action',
      width: 80,
      render: (_, record, index) => (
        <Popconfirm
          title={t('Delete this rule?')}
          onConfirm={() => handleDeleteFileTypeRule(index)}
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small">{t('Delete')}</Button>
        </Popconfirm>
      )
    }
  ];

  const handlePurgeCache = () => {
    setPurgeMode('all');
    setPurgeUrls('');
    setPurgeModalVisible(true);
  };

  const handlePurgeConfirm = async () => {
    setPurging(true);
    try {
      let paths = null;
      if (purgeMode === 'urls') {
        const urlList = purgeUrls
          .split('\n')
          .map(u => u.trim())
          .filter(u => u.length > 0);
        if (urlList.length === 0) {
          message.warning(t('Enter at least one URL'));
          setPurging(false);
          return;
        }
        paths = urlList;
      }
      const response = await cdnService.purgeCache(domainData.id, paths);
      const scope = purgeMode === 'all' ? t('Entire site') : t('{{length}} URLs', { length: (paths || []).length });
      // 后端只入队(队列 + WS 下发到全部节点),几秒内生效
      if (response.success) {
        message.success(t('{{scope}} cache purge request sent', { scope }));
        setPurgeModalVisible(false);
      } else {
        message.error(response.error || t('Failed to purge cache'));
      }
    } catch (error) {
      console.error('Failed to purge cache:', error);
      message.error(t('Failed to purge cache: {{value}}', { value: error?.response?.data?.error || error.message || 'unknown' }));
    } finally {
      setPurging(false);
    }
  };

  // 移动端文件类型规则渲染
  const renderMobileFileTypeRule = (rule, index) => (
    <Card
      key={index}
      size="small"
      style={{ marginBottom: 8 }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Switch
            checked={rule.enabled}
            onChange={(checked) => handleFileTypeRuleChange(index, 'enabled', checked)}
            size="small"
          />
          <Input
            value={t(rule.name)}
            onChange={(e) => handleFileTypeRuleChange(index, 'name', e.target.value)}
            placeholder={t('Rule name')}
            size="small"
            style={{ width: '60%', marginLeft: 8 }}
          />
          <Popconfirm
            title={t('Delete?')}
            onConfirm={() => handleDeleteFileTypeRule(index)}
            okText={t('OK')}
            cancelText={t('Cancel')}
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </div>
      }
    >
      <Form.Item label={t('Extension name')} style={{ marginBottom: 8 }}>
        <TagsInput
          format="comma"
          lowercase
          value={rule.extensions}
          onChange={(v) => handleFileTypeRuleChange(index, 'extensions', v)}
          placeholder="jpg, png, gif"
          size="small"
          validate={(v) => (isExtension(v) ? null : t('Invalid file extension'))}
        />
      </Form.Item>
      <Form.Item label={t('Cache TTL')} style={{ marginBottom: 0 }}>
        <DurationInput
          value={rule.ttl}
          onChange={(value) => handleFileTypeRuleChange(index, 'ttl', value)}
          presets={TTL_PRESETS}
          zeroLabel={t('No cache')}
          min={0}
          max={31536000}
          size="small"
        />
      </Form.Item>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? '12px 0' : '24px 0' }}>
      <Card
        title={
          <Space>
            <DatabaseOutlined />
            <span style={{ fontSize: isMobile ? 14 : 16 }}>{t('Cache settings')}</span>
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
        extra={
          <Button
            icon={<ClearOutlined />}
            onClick={handlePurgeCache}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? t('Clear') : t('Purge cache')}
          </Button>
        }
      >
        <Alert
          message={t('About cache configuration')}
          description={isMobile
            ? t('A well-configured cache policy improves performance and reduces origin load.')
            : t('A well-configured cache policy can significantly improve website performance and reduce origin load. Edge cache is stored on CDN nodes, while browser cache is stored in users\' browsers.')
          }
          type="info"
          showIcon
          closable
          style={{ marginBottom: isMobile ? 16 : 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            cache_enable: 'auto',
            cache_expire: 3600,
            cache_ttl_mode: 'origin_or_default',
            browser_cache: 'auto',
            browser_cache_expire: 7200,
            browser_cache_ttl_mode: 'origin_or_default',
            cache_query_mode: 'standard'
          }}
        >
          <Divider>{t('Edge cache settings')}</Divider>

          <Alert
            message={t('Edge cache')}
            description={t('Cache your content on CDN nodes to reduce origin requests and speed up access.')}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="cache_enable"
            label={t('Edge cache')}
            extra={t('Control whether CDN caches your content')}
          >
            <Select>
              <Option value="auto">
                <Space>
                  <Tag color="blue">{t('Automatic')}</Tag>
                  <span>{t('Determine automatically from response headers')}</span>
                </Space>
              </Option>
              <Option value="on">
                <Space>
                  <Tag color="green">{t('Enable')}</Tag>
                  <span>{t('Force caching')}</span>
                </Space>
              </Option>
              <Option value="off">
                <Space>
                  <Tag color="red">{t('Disable')}</Tag>
                  <span>{t('Do not cache content')}</span>
                </Space>
              </Option>
            </Select>
          </Form.Item>

          {/* Stale Cache 开关 - 当缓存不是禁用时显示 */}
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.cache_enable !== curr.cache_enable}
          >
            {({ getFieldValue }) => {
              const cacheEnabled = getFieldValue('cache_enable');
              if (cacheEnabled === 'off') return null;
              return (
                <Form.Item
                  name="stale_cache_enabled"
                  label={
                    <Space>
                      <span>{t('Serve stale cache on origin failure')}</span>
                      <Tooltip title={t('Serve expired cached content instead of an error page when the origin is unavailable (timeout or 500/502/503/504 errors)')}>
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                  extra={t('Serve cached content when the origin is unavailable, even if the cache has expired, to improve availability')}
                >
                  <Switch checkedChildren={t('Enable')} unCheckedChildren={t('Disable')} />
                </Form.Item>
              );
            }}
          </Form.Item>

          {/* 缓存模式不是禁用时，显示TTL模式和TTL时间设置 */}
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.cache_enable !== curr.cache_enable || prev.cache_ttl_mode !== curr.cache_ttl_mode}
          >
            {({ getFieldValue }) => {
              const cacheEnabled = getFieldValue('cache_enable');
              const ttlMode = getFieldValue('cache_ttl_mode');
              if (cacheEnabled === 'off') return null;

              // 根据TTL模式计算label
              const ttlLabel = ttlMode === 'fixed'
                ? t('Edge cache duration')
                : ttlMode === 'origin'
                  ? t('Fallback cache duration')
                  : t('Default cache duration');

              const ttlExtra = ttlMode === 'fixed'
                ? t('Always use this cache duration')
                : ttlMode === 'origin'
                  ? t('Use this value when the origin does not return Cache-Control')
                  : ttlMode === 'origin_or_default'
                    ? t('Use this default value when the origin does not return Cache-Control')
                    : t('Configured value to compare with the origin TTL');

              return (
                <>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="cache_ttl_mode"
                        label={
                          <Space>
                            <span>{t('Cache TTL mode')}</span>
                            <Tooltip title={t('Control how cache expiration is determined: use a fixed configured value, follow origin response headers, or use the minimum/maximum value')}>
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </Space>
                        }
                      >
                        <Select>
                          <Option value="origin_or_default">
                            <Space>
                              <Tag color="blue">{t('Follow origin or use default')}</Tag>
                              <span>{t('Prefer origin; use configured value if unavailable')}</span>
                            </Space>
                          </Option>
                          <Option value="fixed" disabled={!planAllows(domainData, 'full')}>
                            <Space>
                              <Tag color="green">{t('Fixed')}</Tag>
                              <PlanTag domainData={domainData} need="full" />
                              <span>{t('Always use the configured expiration time')}</span>
                            </Space>
                          </Option>
                          <Option value="origin">
                            <Space>
                              <Tag color="cyan">{t('Follow origin')}</Tag>
                              <span>{t('Always honor origin Cache-Control')}</span>
                            </Space>
                          </Option>
                          <Option value="min" disabled={!planAllows(domainData, 'advanced')}>
                            <Space>
                              <Tag color="orange">{t('Use minimum')}</Tag>
                              <PlanTag domainData={domainData} need="advanced" />
                              <span>{t('Use the lower of the origin and configured values')}</span>
                            </Space>
                          </Option>
                          <Option value="max" disabled={!planAllows(domainData, 'advanced')}>
                            <Space>
                              <Tag color="purple">{t('Use maximum')}</Tag>
                              <PlanTag domainData={domainData} need="advanced" />
                              <span>{t('Use the higher of the origin and configured values')}</span>
                            </Space>
                          </Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="cache_expire"
                        label={ttlLabel}
                        extra={ttlExtra}
                      >
                        <DurationInput presets={TTL_PRESETS} zeroLabel={t('No cache')} min={0} max={31536000} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div style={{ marginBottom: 16 }}>
                    <Space wrap size={[8, 8]}>
                      <Tag color="cyan">{t('Recommended value:')}</Tag>
                      <Button size="small" onClick={() => form.setFieldsValue({ cache_expire: 3600 })}>{t('1 hour')}</Button>
                      <Button size="small" onClick={() => form.setFieldsValue({ cache_expire: 86400 })}>{t('1 day')}</Button>
                      <Button size="small" onClick={() => form.setFieldsValue({ cache_expire: 604800 })}>{t('7 days')}</Button>
                    </Space>
                  </div>
                </>
              );
            }}
          </Form.Item>

          {/* 按文件类型缓存 - 仅当边缘缓存不是禁用时显示 */}
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.cache_enable !== curr.cache_enable}
          >
            {({ getFieldValue }) => {
              const cacheEnabled = getFieldValue('cache_enable');
              if (cacheEnabled === 'off') return null;

              return (
                <>
                  <Divider>{t('Cache by file type')}</Divider>

                  <Alert
                    message={t('File type cache rules')}
                    description={t('Set different cache durations for different file types. These rules override global cache settings. Static assets (images, CSS, JS) can usually be cached longer.')}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  {isMobile ? (
                    <div style={{ marginBottom: 16 }}>
                      {fileTypeRules.map((rule, index) => renderMobileFileTypeRule(rule, index))}
                    </div>
                  ) : (
                    <Table
                      columns={fileTypeColumns}
                      dataSource={fileTypeRules}
                      pagination={false}
                      size="small"
                      rowKey={(record, index) => index}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {fileTypeRules.length === 0 && (
                    <Button type="link" onClick={() => setFileTypeRules(DEFAULT_FILE_TYPE_RULES)} style={{ padding: 0, marginBottom: 8 }}>{t('Fill in recommended rules')}</Button>
                  )}
                  <Button
                    type="dashed"
                    onClick={handleAddFileTypeRule}
                    icon={<PlusOutlined />}
                    block
                    style={{ marginBottom: 24 }}
                  >{t('Add file type rule')}</Button>
                </>
              );
            }}
          </Form.Item>

          <Divider>{t('Browser cache settings')}</Divider>

          <Alert
            message={t('Browser cache')}
            description={t('Set how long browsers cache content to reduce repeat requests.')}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="browser_cache"
            label={t('Browser cache')}
            extra={t('Control whether browsers cache your content')}
          >
            <Select>
              <Option value="auto">
                <Space>
                  <Tag color="blue">{t('Automatic')}</Tag>
                  <span>{t('Determine automatically from response headers')}</span>
                </Space>
              </Option>
              <Option value="on">
                <Space>
                  <Tag color="green">{t('Enable')}</Tag>
                  <span>{t('Force browser caching')}</span>
                </Space>
              </Option>
              <Option value="off">
                <Space>
                  <Tag color="red">{t('Disable')}</Tag>
                  <span>{t('Disable browser caching')}</span>
                </Space>
              </Option>
            </Select>
          </Form.Item>

          {/* 浏览器缓存模式不是禁用时，显示TTL模式和TTL时间设置 */}
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.browser_cache !== curr.browser_cache || prev.browser_cache_ttl_mode !== curr.browser_cache_ttl_mode}
          >
            {({ getFieldValue }) => {
              const browserCacheEnabled = getFieldValue('browser_cache');
              const ttlMode = getFieldValue('browser_cache_ttl_mode');
              if (browserCacheEnabled === 'off') return null;

              // 根据TTL模式计算label
              const ttlLabel = ttlMode === 'fixed'
                ? t('Browser cache duration')
                : ttlMode === 'origin'
                  ? t('Fallback cache duration')
                  : t('Default cache duration');

              const ttlExtra = ttlMode === 'fixed'
                ? t('Always use this cache duration')
                : ttlMode === 'origin'
                  ? t('Use this value when the origin does not return Cache-Control')
                  : ttlMode === 'origin_or_default'
                    ? t('Use this default value when the origin does not return Cache-Control')
                    : t('Configured value to compare with the origin TTL');

              return (
                <>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="browser_cache_ttl_mode"
                        label={
                          <Space>
                            <span>{t('Browser cache duration mode')}</span>
                            <Tooltip title={t('Control how browser cache expiration is determined')}>
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </Space>
                        }
                      >
                        <Select>
                          <Option value="origin_or_default">
                            <Space>
                              <Tag color="blue">{t('Follow origin or use default')}</Tag>
                              <span>{t('Prefer origin; use configured value if unavailable')}</span>
                            </Space>
                          </Option>
                          <Option value="fixed">
                            <Space>
                              <Tag color="green">{t('Fixed')}</Tag>
                              <span>{t('Always use the configured expiration time')}</span>
                            </Space>
                          </Option>
                          <Option value="origin">
                            <Space>
                              <Tag color="cyan">{t('Follow origin')}</Tag>
                              <span>{t('Always honor origin Cache-Control')}</span>
                            </Space>
                          </Option>
                          <Option value="min">
                            <Space>
                              <Tag color="orange">{t('Use minimum')}</Tag>
                              <span>{t('Use the lower of the origin and configured values')}</span>
                            </Space>
                          </Option>
                          <Option value="max">
                            <Space>
                              <Tag color="purple">{t('Use maximum')}</Tag>
                              <span>{t('Use the higher of the origin and configured values')}</span>
                            </Space>
                          </Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="browser_cache_expire"
                        label={ttlLabel}
                        extra={ttlExtra}
                      >
                        <DurationInput presets={TTL_PRESETS} zeroLabel={t('No cache')} min={0} max={31536000} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div style={{ marginBottom: 16 }}>
                    <Space wrap size={[8, 8]}>
                      <Tag color="cyan">{t('Recommended value:')}</Tag>
                      <Button size="small" onClick={() => form.setFieldsValue({ browser_cache_expire: 7200 })}>{t('2 hours')}</Button>
                      <Button size="small" onClick={() => form.setFieldsValue({ browser_cache_expire: 86400 })}>{t('1 day')}</Button>
                      <Button size="small" onClick={() => form.setFieldsValue({ browser_cache_expire: 604800 })}>{t('7 days')}</Button>
                    </Space>
                  </div>
                </>
              );
            }}
          </Form.Item>

          {/* 缓存键设置 - 仅当边缘缓存不是禁用时显示 */}
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.cache_enable !== curr.cache_enable || prev.cache_query_mode !== curr.cache_query_mode}
          >
            {({ getFieldValue }) => {
              const cacheEnabled = getFieldValue('cache_enable');
              if (cacheEnabled === 'off') return null;

              const queryMode = getFieldValue('cache_query_mode');

              return (
                <>
                  <Divider>
                    <Space>
                      <KeyOutlined />{t('Cache key settings')}<PlanTag domainData={domainData} need={keyLocked ? 'advanced' : null} /></Space>
                  </Divider>

                  <Alert
                    message={t('Cache key configuration')}
                    description={t('The cache key determines how cached versions are distinguished. Proper configuration can improve the cache hit ratio while ensuring users receive the correct content.')}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <ConfigProvider componentDisabled={keyLocked}>
                  <Form.Item
                    name="cache_query_mode"
                    label={
                      <Space>
                        <span>{t('Query string handling mode')}</span>
                        <Tooltip title={t('Control how the query string in a URL affects the cache key')}>
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </Space>
                    }
                    extra={t('Control how the ?key=value portion of a URL affects caching')}
                  >
                    <Select>
                      <Option value="standard">
                        <Space>
                          <Tag color="blue">{t('Standard')}</Tag>
                          <span>{t('Keep all query parameters')}</span>
                        </Space>
                      </Option>
                      <Option value="ignore">
                        <Space>
                          <Tag color="orange">{t('Ignore')}</Tag>
                          <span>{t('Ignore the entire query string')}</span>
                        </Space>
                      </Option>
                      <Option value="sorted">
                        <Space>
                          <Tag color="green">{t('Sort')}</Tag>
                          <span>{t('Sort by parameter name before caching (recommended)')}</span>
                        </Space>
                      </Option>
                      <Option value="whitelist">
                        <Space>
                          <Tag color="cyan">{t('Allowlist')}</Tag>
                          <span>{t('Keep only specified parameters')}</span>
                        </Space>
                      </Option>
                      <Option value="blacklist">
                        <Space>
                          <Tag color="red">{t('Blacklist')}</Tag>
                          <span>{t('Exclude specified parameters')}</span>
                        </Space>
                      </Option>
                    </Select>
                  </Form.Item>

                  {(queryMode === 'whitelist' || queryMode === 'blacklist') && (
                    <Form.Item
                      label={queryMode === 'whitelist' ? t('Query parameter allowlist') : t('Query parameter blocklist')}
                    >
                      <TagsInput
                        format="comma"
                        value={queryParams}
                        onChange={(v) => setQueryParams(v)}
                        validate={(v) => (isQueryParam(v) ? null : t('Invalid parameter name'))}
                        placeholder={queryMode === 'whitelist' ? t('Enter the parameter names to keep') : t('Enter the parameter names to exclude')}
                      />
                    </Form.Item>
                  )}

                  <Row gutter={[16, 16]}>
                    <Col xs={12} sm={8}>
                      <Form.Item
                        label={
                          <Space>
                            <span>{isMobile ? t('By device') : t('Vary by device type')}</span>
                            <Tooltip title={t('Cache different versions for mobile and desktop')}>
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </Space>
                        }
                      >
                        <Switch
                          checked={cacheKey.vary_by_device}
                          onChange={(checked) => handleCacheKeyChange('vary_by_device', checked)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={8}>
                      <Form.Item
                        label={
                          <Space>
                            <span>{isMobile ? t('By country') : t('Vary by country')}</span>
                            <Tooltip title={t('Cache different versions based on visitor country/region')}>
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </Space>
                        }
                      >
                        <Switch
                          checked={cacheKey.vary_by_country}
                          onChange={(checked) => handleCacheKeyChange('vary_by_country', checked)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={8}>
                      <Form.Item
                        label={
                          <Space>
                            <span>{isMobile ? t('By language') : t('Vary by language')}</span>
                            <Tooltip title={t('Cache different language versions based on the Accept-Language request header')}>
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </Space>
                        }
                      >
                        <Switch
                          checked={cacheKey.vary_by_language}
                          onChange={(checked) => handleCacheKeyChange('vary_by_language', checked)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label={
                      <Space>
                        <span>{t('Cache host mode')}</span>
                        <Tooltip title={t('Determines which hostname is used in the cache key')}>
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </Space>
                    }
                    extra={t('Control which domain is used in the cache key')}
                  >
                    <Select
                      value={cacheKey.cache_host_mode === 'resolved' ? 'original' : cacheKey.cache_host_mode}
                      onChange={(value) => handleCacheKeyChange('cache_host_mode', value)}
                    >
                      <Option value="original">
                        <Space>
                          <Tag color="blue">{t('Visitor hostname')}</Tag>
                          <span>{t('Each hostname keeps its own cache')}</span>
                        </Space>
                      </Option>
                      <Option value="host_header">
                        <Space>
                          <Tag color="cyan">{t('Origin Host')}</Tag>
                          <span>{t('Hostnames with the same origin Host share one cache')}</span>
                        </Space>
                      </Option>
                    </Select>
                  </Form.Item>

                  </ConfigProvider>

                  <CacheKeyPreview
                    queryMode={queryMode}
                    queryParams={queryParams}
                    cacheKey={cacheKey}
                    isMobile={isMobile}
                  />
                </>
              );
            }}
          </Form.Item>

          {/* 高级缓存 / 负缓存 —— settings.cache_key.{status_ttl,min_ttl,max_ttl,stale_if_error,bypass_*} */}
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.cache_enable !== curr.cache_enable}
          >
            {({ getFieldValue }) => {
              if (getFieldValue('cache_enable') === 'off') return null;
              return (
                <>
                  <Divider>{t('Advanced caching / Negative caching')}<PlanTag domainData={domainData} need={ttlLocked ? 'advanced' : null} /></Divider>

                  <Alert
                    message={t('Negative caching & TTL limits')}
                    description={t('Set cache durations by response status code (negative caching), with minimum and maximum limits on the final duration. Serve stale content within the grace period if the origin fails.')}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <ConfigProvider componentDisabled={ttlLocked}>
                  <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('Cache by status code (negative caching)')}</div>
                  <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: isMobile ? 12 : 13 }}>{t('Set cache durations in seconds for specific HTTP status codes (e.g. 404, 500, 301). These take priority over other cache duration settings. Caching 404/500 responses prevents repeated requests to the origin for errors.')}</div>
                  {statusTtlRules.map((rule, index) => (
                    <Row gutter={8} key={index} style={{ marginBottom: 8 }} align="middle">
                      <Col xs={11} sm={8}>
                        <InputNumber
                          value={rule.code}
                          onChange={(v) => handleStatusTtlChange(index, 'code', v)}
                          min={100}
                          max={599}
                          placeholder={t('Status code')}
                          style={{ width: '100%' }}
                          addonBefore={t('Status code')}
                        />
                      </Col>
                      <Col xs={9} sm={8}>
                        <DurationInput
                          value={rule.ttl}
                          onChange={(v) => handleStatusTtlChange(index, 'ttl', v)}
                          presets={[10, 30, 60, 300, 600, 3600]}
                          zeroLabel={t('No cache')}
                          min={0}
                          max={31536000}
                        />
                      </Col>
                      <Col xs={4}>
                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteStatusTtl(index)} />
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" onClick={handleAddStatusTtl} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>{t('Add status code rule')}</Button>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>{t('Minimum cache duration (min_ttl)')}</span><Tooltip title={t('Minimum cache TTL (0 means unlimited). Only increases TTL values greater than 0')}><QuestionCircleOutlined /></Tooltip></Space>}>
                        <DurationInput value={cacheKey.min_ttl} onChange={(v) => handleCacheKeyChange('min_ttl', v ?? 0)} presets={[60, 300, 3600, 86400, 604800]} zeroLabel={t('No limit')} min={0} max={31536000} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>{t('Maximum cache duration (max_ttl)')}</span><Tooltip title={t('Maximum cache TTL (0 means unlimited)')}><QuestionCircleOutlined /></Tooltip></Space>}>
                        <DurationInput value={cacheKey.max_ttl} onChange={(v) => handleCacheKeyChange('max_ttl', v ?? 0)} presets={[3600, 86400, 604800, 2592000, 31536000]} zeroLabel={t('No limit')} min={0} max={31536000} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>{t('Error grace period (stale_if_error)')}</span><Tooltip title={t('Continue serving stale cache for this many seconds when the origin fails')}><QuestionCircleOutlined /></Tooltip></Space>}>
                        <DurationInput value={cacheKey.stale_if_error} onChange={(v) => handleCacheKeyChange('stale_if_error', v ?? 0)} presets={[60, 300, 3600, 86400, 604800]} zeroLabel={t('Off')} min={0} max={31536000} />
                      </Form.Item>
                    </Col>
                  </Row>
                  </ConfigProvider>

                  <Divider orientation="left" style={{ margin: '4px 0 12px' }}>{t('Cache bypass')}</Divider>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>{t('Bypass cache with Authorization header')}</span><Tooltip title={t('Bypass cache and go directly to the origin when the request contains an Authorization header')}><QuestionCircleOutlined /></Tooltip></Space>}>
                        <Switch checked={!!cacheKey.bypass_auth} onChange={(c) => handleCacheKeyChange('bypass_auth', c)} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>{t('Honor client no-cache')}</span><Tooltip title={t('Bypass cache when client requests include Cache-Control: no-cache')}><QuestionCircleOutlined /></Tooltip></Space>}>
                        <Switch checked={cacheKey.respect_client_no_cache === true} onChange={(c) => handleCacheKeyChange('respect_client_no_cache', c)} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>{t('Session cookies that bypass cache')}</span><Tooltip title={t('Bypass cache when these Cookie names are present (e.g., session, token)')}><QuestionCircleOutlined /></Tooltip></Space>}>
                        <TagsInput format="array" value={cacheKey.bypass_cookies || []} onChange={(v) => handleCacheKeyChange('bypass_cookies', v)} placeholder="session, wordpress_logged_in" validate={(v) => (isCookieName(v) ? null : t('Invalid cookie name'))} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              );
            }}
          </Form.Item>

          {/* 回源防护 / Origin Shield —— settings.origin_shield.* */}
          <Divider>{t('Origin Shield')}</Divider>
          <Alert
            message={t('Origin protection')}
            description={t('Limit concurrent origin requests per domain. When the threshold is exceeded, protect the origin with actions in sequence: serve cache first → queue → human verification challenge → block.')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item label={<Space><span>{t('Max concurrent origin requests')}</span><Tooltip title={t('Maximum concurrent origin requests per domain; 0 means unlimited')}><QuestionCircleOutlined /></Tooltip></Space>}>
                <CountInput value={originShield.max_concurrency} onChange={(v) => handleOriginShieldChange('max_concurrency', v ?? 0)} presets={[10, 50, 100, 500, 1000]} zeroLabel={t('Unlimited')} min={0} max={1000000} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={<Space><span>{t('Over-limit actions (sequence)')}</span><Tooltip title={t('Actions performed in sequence when the concurrency limit is exceeded')}><QuestionCircleOutlined /></Tooltip></Space>}>
                <LadderSelect value={originShield.action} onChange={(v) => handleOriginShieldChange('action', v)} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Form.Item label={<Space><span>{t('Maximum queue length')}</span><Tooltip title={t('Maximum number of queued requests for the queue action')}><QuestionCircleOutlined /></Tooltip></Space>}>
                <CountInput value={originShield.queue_max} onChange={(v) => handleOriginShieldChange('queue_max', v ?? 0)} presets={[50, 200, 500, 1000, 5000]} zeroLabel={t('Default (100)')} min={0} max={1000000} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={<Space><span>{t('Queue wait')}</span><Tooltip title={t('Maximum wait time per request in milliseconds for the queue action')}><QuestionCircleOutlined /></Tooltip></Space>}>
                <DurationInput base="ms" value={originShield.queue_wait_ms} onChange={(v) => handleOriginShieldChange('queue_wait_ms', v ?? 0)} presets={[1000, 3000, 5000, 10000, 30000]} zeroLabel={t('Default (500 ms)')} min={0} max={600000} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={<Space><span>{t('Retry-After')}</span><Tooltip title={t('Retry-After in seconds returned to the client for block / challenge actions')}><QuestionCircleOutlined /></Tooltip></Space>}>
                <DurationInput value={originShield.retry_after} onChange={(v) => handleOriginShieldChange('retry_after', v ?? 0)} presets={[5, 10, 30, 60, 300, 600]} zeroLabel={t('Default (1 s)')} min={0} max={86400} />
              </Form.Item>
            </Col>
          </Row>
          {(originShield.action || []).includes('waiting_room') && (
            <Folded label={t('Waiting room')}>
              <WaitingRoomFields
                value={originShield.waiting_room}
                onChange={(v) => handleOriginShieldChange('waiting_room', Object.fromEntries(Object.entries(v).filter(([, x]) => x !== null && x !== undefined && x !== '')))}
              />
            </Folded>
          )}

          <Divider />

          <Alert
            message={t('Notice')}
            description={t('Changing cache settings does not immediately invalidate existing cache. To apply changes immediately, click Clear cache in the upper right.')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
              size="large"
            >{t('Save settings')}</Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 缓存清除弹窗 */}
      <Modal
        title={<Space><ClearOutlined />{t('Purge cache')}</Space>}
        open={purgeModalVisible}
        onCancel={() => setPurgeModalVisible(false)}
        onOk={handlePurgeConfirm}
        confirmLoading={purging}
        okText={t('Confirm purge')}
        cancelText={t('Cancel')}
      >
        <Alert
          message={t('After the purge, CDN nodes will fetch the latest content from the origin again.')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Radio.Group
          value={purgeMode}
          onChange={e => setPurgeMode(e.target.value)}
          style={{ marginBottom: 16 }}
        >
          <Radio value="all">{t('Purge all site cache')}</Radio>
          <Radio value="urls">{t('Purge by URL')}</Radio>
        </Radio.Group>
        {purgeMode === 'urls' && (
          <TagsInput
            format="lines"
            commaSeparates={false}
            placeholder="https://example.com/image.png"
            value={purgeUrls}
            onChange={(v) => setPurgeUrls(v)}
            validate={(v) => (isUrlOrPath(v) ? null : t('Use a full URL or a path starting with /'))}
          />
        )}
      </Modal>
    </div>
  );
}
